import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import type { FC, ReactNode } from "react";

/**
 * Presentational full-screen status states rendered by the app shell while a
 * session is loading, errored, or unauthorized. Pure props-in so they render
 * standalone in Storybook.
 */

export const FullScreenLoader: FC = () => {
  return (
    <div className="flex h-screen flex-col items-center justify-center">
      <Loader style={LoaderStyle.ZOOMIES} />
    </div>
  );
};

export const FullScreenMessage: FC<{ children: ReactNode }> = ({
  children,
}) => {
  return (
    <div className="flex h-screen flex-col items-center justify-center">
      <div className="max-w-xl text-center text-2xl text-foreground">
        {children}
      </div>
    </div>
  );
};

/**
 * Shown when the Supabase session never finishes loading — most often a frozen
 * tab holding the auth Web Lock. Refreshing re-runs initialization, which now
 * gives up on a stuck lock instead of hanging.
 */
export const SessionLoadStalledScreen: FC<{ onReload: () => void }> = ({
  onReload,
}) => {
  return (
    <FullScreenMessage>
      We&apos;re having trouble signing you in. Please&nbsp;
      <button className="text-blue-500 hover:text-blue-700" onClick={onReload}>
        refresh
      </button>{" "}
      to try again.
    </FullScreenMessage>
  );
};

/**
 * Shown when resolving the session returns a 401 — refreshing won't help, so we
 * offer a sign-out + retry.
 */
export const NotAuthorizedScreen: FC<{ onSignOut: () => void }> = ({
  onSignOut,
}) => {
  return (
    <FullScreenMessage>
      You are not authorized to access this application. Please&nbsp;
      <button className="text-blue-500 hover:text-blue-700" onClick={onSignOut}>
        sign out
      </button>{" "}
      and try again with a different email or sign-in method.
    </FullScreenMessage>
  );
};
